/**
 * #261 P3 / #113 — Reine Logik für die ValiSight-Alert-Regel-Verwaltung.
 * Bewusst ohne React-/IO-Imports → direkt unit-testbar.
 *
 * `buildRulePayload` spiegelt das Backend-Zod-Schema (valisight
 * alertRules.controller.js `createSchema`). Grund: die kanal-spezifischen
 * Pflichtfelder (telegram braucht botToken+chatId, email braucht to, alle
 * anderen brauchen url) sollen dem Operator SOFORT im Formular gemeldet werden,
 * statt erst über einen 400-Roundtrip. `channelConfigFields` ist dabei die
 * einzige Wahrheit für „welche Felder gehören zu welchem Kanal" — dasselbe
 * Ergebnis steuert Formular-Rendering UND Validierung, damit beide nie
 * auseinanderlaufen.
 */

export const SEVERITIES = ['info', 'warn', 'error', 'critical'];
export const CHANNELS = ['webhook', 'slack', 'discord', 'telegram', 'email'];
export const MATCH_TYPES = ['substring', 'regex'];

// Backend: z.coerce.number().int().min(0).max(86_400).default(900)
const COOLDOWN_MIN = 0;
const COOLDOWN_MAX = 86_400;
const COOLDOWN_DEFAULT = 900;

/**
 * Welche channelConfig-Felder ein Kanal zwingend braucht. Steuert Formular +
 * Validierung gemeinsam.
 * @param {string} channel
 * @returns {string[]}
 */
export function channelConfigFields(channel) {
  if (channel === 'telegram') return ['botToken', 'chatId'];
  if (channel === 'email') return ['to'];
  return ['url']; // webhook / slack / discord
}

/**
 * Baut aus dem Formular-State den POST-Body für `POST /v1/alert-rules` und
 * validiert kanal-spezifisch (Spiegel des Backend-Schemas). Gibt bei einem
 * Fehler die erste sprechende Meldung zurück, statt einen 400 zu provozieren.
 *
 * @param {object} form
 * @returns {{ ok: true, payload: object } | { ok: false, error: string }}
 */
export function buildRulePayload(form = {}) {
  const name = String(form.name ?? '').trim();
  if (!name) return { ok: false, error: 'Name ist Pflicht (1–190 Zeichen).' };
  if (name.length > 190) return { ok: false, error: 'Name ist zu lang (max. 190 Zeichen).' };

  const channel = String(form.channel ?? '').trim();
  if (!CHANNELS.includes(channel)) {
    return { ok: false, error: `Unbekannter Kanal „${channel || '—'}". Erlaubt: ${CHANNELS.join(', ')}.` };
  }

  // minSeverity: leer → Backend-Default 'error'.
  const minSeverity = form.minSeverity ? String(form.minSeverity) : 'error';
  if (!SEVERITIES.includes(minSeverity)) {
    return { ok: false, error: `Unbekannter Schweregrad „${minSeverity}".` };
  }

  // Kanal-spezifische Pflichtfelder prüfen und nur die relevanten übernehmen —
  // so wandert z. B. eine versehentlich gefüllte url bei telegram nicht mit.
  const channelConfig = {};
  for (const field of channelConfigFields(channel)) {
    const raw = form[field];
    const value = raw == null ? '' : String(raw).trim();
    if (!value) {
      return { ok: false, error: missingFieldMessage(channel) };
    }
    channelConfig[field] = value;
  }

  // matchType optional; wenn gesetzt, ist pattern Pflicht (Backend-Regel).
  let matchType;
  let pattern;
  if (form.matchType) {
    matchType = String(form.matchType);
    if (!MATCH_TYPES.includes(matchType)) {
      return { ok: false, error: `Unbekannter matchType „${matchType}". Erlaubt: ${MATCH_TYPES.join(', ')}.` };
    }
    pattern = String(form.pattern ?? '').trim();
    if (!pattern) return { ok: false, error: 'matchType gesetzt, aber Muster (pattern) fehlt.' };
    if (pattern.length > 1000) return { ok: false, error: 'Muster ist zu lang (max. 1000 Zeichen).' };
  }

  const payload = { name, minSeverity, channel, channelConfig, cooldownSecs: normalizeCooldown(form.cooldownSecs) };
  if (matchType) { payload.matchType = matchType; payload.pattern = pattern; }
  // tenantId/sourceId nur mitschicken, wenn explizit gesetzt (interne
  // Provisionierung für einen bestimmten Tenant/Quelle); sonst tenant-weit.
  const tenantId = toOptionalInt(form.tenantId);
  if (tenantId != null) payload.tenantId = tenantId;
  const sourceId = toOptionalInt(form.sourceId);
  if (sourceId != null) payload.sourceId = sourceId;

  return { ok: true, payload };
}

function missingFieldMessage(channel) {
  if (channel === 'telegram') return 'Telegram braucht botToken und chatId.';
  if (channel === 'email') return 'E-Mail braucht eine Empfänger-Adresse (to).';
  return `${channel} braucht eine Incoming-Webhook-URL (url).`;
}

// Cooldown robust auf eine ganze Sekundenzahl im erlaubten Fenster bringen.
// Leer/ungültig → Backend-Default (900s). Spiegelt z.coerce.number().int().
export function normalizeCooldown(raw) {
  if (raw === '' || raw == null) return COOLDOWN_DEFAULT;
  const n = Number(raw);
  if (!Number.isFinite(n)) return COOLDOWN_DEFAULT;
  const int = Math.trunc(n);
  if (int < COOLDOWN_MIN) return COOLDOWN_MIN;
  if (int > COOLDOWN_MAX) return COOLDOWN_MAX;
  return int;
}

function toOptionalInt(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

/**
 * Normalisiert eine vom Backend gelistete (bereits maskierte) Regel für die
 * Tabellen-Anzeige. Tolerant gegen fehlende Felder, da `maskConfig` im Backend
 * je nach Kanal nur eine Teilmenge zurückgibt (z. B. keine E-Mail-Adresse).
 *
 * @param {object} rule  Ergebnis-Element aus GET /v1/alert-rules
 */
export function summarizeRule(rule = {}) {
  const cfg = rule.channelConfig || {};
  return {
    id: rule.id,
    name: rule.name || '—',
    channel: rule.channel || '—',
    minSeverity: rule.minSeverity || 'error',
    enabled: !!rule.enabled,
    cooldownMin: Math.round((Number(rule.cooldownSecs) || 0) / 60),
    match: rule.matchType ? `${rule.matchType}: ${rule.pattern ?? ''}` : 'alle',
    target: ruleTarget(rule.channel, cfg),
  };
}

// Kurzbeschreibung des Zustellziels aus der maskierten Config. Secrets bleiben
// maskiert (das Backend hat url/botToken bereits verschleiert).
function ruleTarget(channel, cfg) {
  if (channel === 'telegram') return cfg.chatId ? `chat ${cfg.chatId}` : '(maskiert)';
  if (channel === 'email') return cfg.to || '(maskiert)';
  return cfg.url || '(maskiert)';
}

/**
 * Übersetzt das Ergebnis von `POST /v1/alert-rules/:id/test` in einen
 * Anzeige-Text.
 * @param {{ ok?: boolean, error?: string|null }} res
 */
export function describeTestResult(res = {}) {
  return res.ok
    ? 'Test-Alarm gesendet — der Kanal funktioniert.'
    : `Test fehlgeschlagen: ${res.error || 'unbekannter Fehler'}`;
}
